// Volume editor: panel list + fields, on-canvas handles, placing, save/load.
import type { Viewer } from './viewer.ts';
import { download } from './download.ts';
import * as cache from './cache.ts';
import { type V3, add, scale, sub, dot, project, niceLength } from './math.ts';
import { type Volume, type Kind, KINDS, KIND_COLOR, MAX_VOLUMES, rotation, axis, boxPoint, edges, parseVolumes, toFile, nextId } from './volumes.ts';

type Drag =
  | { t: 'face'; k: number; s: number; x0: number; y0: number; v0: Volume; sv: [number, number] }
  | { t: 'move'; x0: number; y0: number; v0: Volume; k: number }
  | { t: 'rot'; v0: Volume; a0: number };

const el = (tag: string, attrs: Record<string, string> = {}, text = '') => {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  if (text) e.textContent = text;
  return e;
};
const copy = (v: Volume): Volume => ({ ...v, center: [...v.center], size: [...v.size], ypr: [...v.ypr], clouds: [...v.clouds] });
const LABEL: Record<Kind, string> = { hide: 'Hide', align: 'Align', solid: 'Solid' };
const TIP: Record<Kind, string> = {
  hide: 'points inside are not drawn (a solid box wins over this)',
  align: 'points inside are what the alignment uses to fit scans',
  solid: 'a known wall/floor: permanent, never hidden',
};

export class VolumeEditor {
  sel: Volume | null = null;
  placing = false;
  kind: Kind = 'hide';
  private drag: Drag | null = null;
  private handles: HTMLElement[] = [];   // 0..5 faces (+x -x +y -y +z -z), 6 move, 7 rotate
  private saveTimer = 0;
  private storeKey: string;

  constructor(private v: Viewer, private canvas: HTMLCanvasElement, private base: string, private $: (id: string) => HTMLElement) {
    this.storeKey = 'cloudview.volumes:' + base;
    const box = $('vhandles');
    for (let i = 0; i < 8; i++) {
      const h = el('div', { class: i < 6 ? 'vh face' : i === 6 ? 'vh move' : 'vh rot' }, i === 7 ? '⟳' : '');
      h.title = i < 6 ? 'drag to resize' : i === 6 ? 'drag to move' : 'drag to turn (shift: 15° steps)';
      h.addEventListener('pointerdown', e => this.down(e, i));
      h.addEventListener('pointermove', e => this.move(e));
      h.addEventListener('pointerup', () => this.up());
      h.addEventListener('pointercancel', () => this.up());
      box.append(h);
      this.handles.push(h);
    }
    $('vadd').onclick = () => this.setPlacing(!this.placing);
    $('vexport').onclick = () => this.exportFile();
    $('vimport').onclick = () => (this.$('vfile') as HTMLInputElement).click();
    $('vfile').onchange = () => this.importFile();
    const show = $('vshow') as HTMLInputElement, tint = $('vtint') as HTMLInputElement;
    show.onchange = () => { v.s.volShow = show.checked; v.dirty = true; };
    tint.onchange = () => { v.s.volTint = tint.checked ? 0.35 : 0; v.dirty = true; };
    for (const k of KINDS) {
      const b = el('button', { class: 'small', 'data-k': k, title: `new boxes: ${TIP[k]}` }, LABEL[k]);
      b.onclick = () => { this.kind = k; this.syncKindButtons(); };
      $('vkinds').append(b);
    }
    this.syncKindButtons();
    addEventListener('keydown', e => {
      if ((e.target as HTMLElement).closest?.('input,select,textarea')) return;
      if (e.code === 'Escape') { if (this.placing) this.setPlacing(false); else if (this.sel) this.select(null); }
      if ((e.code === 'Delete' || e.code === 'Backspace') && this.sel) { e.preventDefault(); this.remove(this.sel); }
    });
  }

  get vols() { return this.v.s.volumes; }

  /** Saved edits in this browser win; otherwise the dataset's volumes.json, if any. */
  async load() {
    let saved: string | null = null;
    try { saved = localStorage.getItem(this.storeKey); } catch { /* storage blocked */ }
    try {
      if (saved) this.v.s.volumes = parseVolumes(JSON.parse(saved));
      else {
        this.v.s.volumes = parseVolumes(await cache.fetchFreshJson(this.base + 'volumes.json'));
      }
    } catch (e) { console.warn('volumes:', e); }
    this.changed(false);
  }

  private save() {
    clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      try { localStorage.setItem(this.storeKey, JSON.stringify(toFile(this.vols, this.v.scene?.name))); } catch { /* storage blocked */ }
    }, 300) as unknown as number;
  }

  private syncKindButtons() {
    for (const b of this.$('vkinds').querySelectorAll<HTMLElement>('button')) b.classList.toggle('on', b.dataset.k === this.kind);
  }

  setPlacing(on: boolean) {
    this.placing = on && this.vols.length < MAX_VOLUMES;
    this.$('vadd').classList.toggle('on', this.placing);
    this.$('vadd').textContent = this.placing ? 'Click the cloud…' : '+ Box';
  }

  /** Tap while placing: a 1 m box at the point (plan: ~100 px footprint, floor to top). */
  place(x: number, y: number): boolean {
    if (!this.placing) return false;
    const c = this.v.cam, h = this.v.pickAt(x, y);
    let vol: Volume;
    const base = { id: nextId(this.vols), name: '', kind: this.kind, on: true, ypr: [0, 0, 0] as V3, clouds: [] };
    if (c.ortho) {
      const p = h?.p ?? c.unproject(x, y, 0), z0 = this.v.floorZ - 0.2, z1 = this.v.bbox[5] + 0.2;
      const w = Math.max(0.5, niceLength(c.ps * 120));   // about 100 px across
      vol = { ...base, center: [p[0], p[1], (z0 + z1) / 2], size: [w, w, z1 - z0] };
    } else {
      if (!h) return true;
      vol = { ...base, center: h.p, size: [1, 1, 1] };
    }
    this.vols.push(vol);
    this.setPlacing(false);
    this.select(vol);
    return true;
  }

  select(vol: Volume | null) {
    this.sel = vol;
    this.changed();
  }

  remove(vol: Volume) {
    this.v.s.volumes = this.vols.filter(o => o !== vol);
    if (this.sel === vol) this.sel = null;
    this.changed();
  }

  /** Anything about the volumes changed: lines, list, editor, render, save. */
  changed(persist = true) {
    if (this.sel && !this.vols.includes(this.sel)) this.sel = null;
    this.lines();
    this.buildList();
    this.buildEditor();
    this.v.dirty = true;
    if (persist) this.save();
  }

  /** A field or a drag moved the selected box: cheaper than changed(). */
  private edited() {
    this.lines();
    this.syncEditor();
    this.v.dirty = true;
    this.save();
  }

  private lines() {
    const v = this.v;
    v.lines = v.lines.filter(l => {
      if (!l.id.startsWith('vol:')) return true;
      if (l.buf) v.gl.deleteBuffer(l.buf);
      return false;
    });
    for (const vol of this.vols) {
      const a = vol === this.sel ? 1 : vol.on ? 0.6 : 0.2;
      v.setLines('vol:' + vol.id, new Float32Array(edges(vol)), [...KIND_COLOR[vol.kind], a] as [number, number, number, number], true, false);
    }
  }

  private buildList() {
    const box = this.$('vlist');
    box.textContent = '';
    this.vols.forEach((vol, i) => {
      const row = el('div', { class: 'vol' + (vol === this.sel ? ' sel' : '') });
      const on = el('input', { type: 'checkbox', title: 'enabled' }) as HTMLInputElement;
      on.checked = vol.on;
      on.onchange = () => { vol.on = on.checked; this.changed(); };
      const sw = el('span', { class: 'sw' });
      sw.style.background = `rgb(${KIND_COLOR[vol.kind].map(x => (x * 255) | 0).join(',')})`;
      const name = el('span', { class: 'name' }, `${LABEL[vol.kind]} · ${vol.name || vol.id}`);
      row.title = `${vol.size.map(s => s.toFixed(2)).join(' × ')} m${vol.clouds.length ? ` · ${vol.clouds.length} cloud(s)` : ''}`;
      row.onclick = e => { if (e.target !== on) this.select(vol === this.sel ? null : vol); };
      row.append(on, sw, name);
      box.append(row);
      if (i === MAX_VOLUMES - 1) box.append(el('div', { class: 'help' }, `at most ${MAX_VOLUMES} boxes`));
    });
    if (!this.vols.length) box.append(el('div', { class: 'help' }, 'No boxes yet. Pick a kind, press + Box, click the cloud.'));
  }

  private fields: Record<string, HTMLInputElement> = {};

  private buildEditor() {
    const box = this.$('vedit'), vol = this.sel;
    box.textContent = '';
    this.fields = {};
    box.hidden = !vol;
    if (!vol) return;
    const name = el('input', { type: 'text', placeholder: vol.id, value: vol.name, maxlength: '80' }) as HTMLInputElement;
    name.oninput = () => { vol.name = name.value; this.save(); };
    name.onchange = () => this.buildList();
    const kind = el('select') as HTMLSelectElement;
    for (const k of KINDS) kind.append(el('option', { value: k, title: TIP[k] }, LABEL[k]));
    kind.value = vol.kind;
    kind.onchange = () => { vol.kind = kind.value as Kind; this.changed(); };
    box.append(el('label', {}, 'Name'), name, el('label', {}, 'Kind'), kind);
    box.querySelectorAll('label').forEach(l => l.classList.add('k'));
    const grid = el('div', { class: 'vgrid' });
    grid.append(el('span'), ...['x / yaw', 'y / pitch', 'z / roll'].map(t => el('span', { class: 'hd' }, t)));
    const row = (label: string, key: 'center' | 'size' | 'ypr', step: string) => {
      grid.append(el('span', { class: 'k' }, label));
      for (let k = 0; k < 3; k++) {
        const i = el('input', { type: 'number', step }) as HTMLInputElement;
        i.oninput = () => {
          const x = parseFloat(i.value);
          if (!Number.isFinite(x) || (key === 'size' && x <= 0)) return;
          vol[key][k] = x;
          this.edited();
        };
        this.fields[key + k] = i;
        grid.append(i);
      }
    };
    row('Centre m', 'center', '0.05');
    row('Size m', 'size', '0.05');
    row('Turn °', 'ypr', '1');
    box.append(grid);
    // which clouds it applies to; all ticked = all (stored as an empty list)
    const cl = el('div', { class: 'vclouds' });
    cl.append(el('span', { class: 'k' }, 'Applies to'));
    for (const c of this.v.clouds) {
      const l = el('label', { title: c.meta.name });
      const i = el('input', { type: 'checkbox' }) as HTMLInputElement;
      i.checked = !vol.clouds.length || vol.clouds.includes(c.meta.id);
      i.onchange = () => {
        const ticked = [...cl.querySelectorAll<HTMLInputElement>('input')].map((x, j) => (x.checked ? this.v.clouds[j].meta.id : '')).filter(Boolean);
        vol.clouds = ticked.length === this.v.clouds.length ? [] : ticked;
        this.changed();
      };
      const sw = el('span', { class: 'sw' });
      sw.style.background = c.meta.color;
      l.append(i, sw, document.createTextNode(c.meta.name.replace(/^RTAB /, '')));
      cl.append(l);
    }
    box.append(cl);
    const btns = el('div', { class: 'row' });
    const level = el('button', { class: 'small', title: 'pitch and roll to 0' }, 'Level');
    level.onclick = () => { vol.ypr[1] = vol.ypr[2] = 0; this.edited(); };
    const dup = el('button', { class: 'small' }, 'Duplicate');
    dup.onclick = () => {
      if (this.vols.length >= MAX_VOLUMES) return;
      const d = copy(vol);
      d.id = nextId(this.vols); d.name = vol.name ? vol.name + ' copy' : '';
      d.center = add(d.center, boxAxisOffset(vol));
      this.vols.push(d); this.select(d);
    };
    const del = el('button', { class: 'small', title: 'Delete key' }, 'Delete');
    del.onclick = () => this.remove(vol);
    btns.append(level, dup, del);
    box.append(btns);
    this.syncEditor();
  }

  private syncEditor() {
    const vol = this.sel;
    if (!vol) return;
    for (const key of ['center', 'size', 'ypr'] as const) for (let k = 0; k < 3; k++) {
      const i = this.fields[key + k];
      if (i && document.activeElement !== i) i.value = String(+vol[key][k].toFixed(key === 'ypr' ? 1 : 3));
    }
  }

  // ── handles ─────────────────────────────────────────────────────────────
  private screen(p: V3): [number, number] | null {
    const c = this.v.cam, q = project(c.vp, p);
    return q[3] > 0 && Math.abs(q[2]) <= 1 ? [((q[0] + 1) / 2) * c.w, ((1 - q[1]) / 2) * c.h] : null;
  }

  /** Per rendered frame: put the handles on the selected box. */
  updateHandles() {
    const vol = this.sel;
    const box = this.$('vhandles');
    box.hidden = !vol;
    if (!vol) return;
    const R = rotation(vol.ypr), c = this.screen(vol.center);
    const put = (h: HTMLElement, s: [number, number] | null) => {
      h.style.display = s ? '' : 'none';
      if (s) h.style.transform = `translate(${s[0]}px,${s[1]}px)`;
    };
    put(this.handles[6], c);
    for (let i = 0; i < 6; i++) {
      const u: V3 = [0, 0, 0];
      u[i >> 1] = i & 1 ? -1 : 1;
      const s = this.screen(boxPoint(vol, u, R));
      // a face seen edge-on lands on the centre handle; leave it out
      put(this.handles[i], s && c && Math.hypot(s[0] - c[0], s[1] - c[1]) > 14 ? s : null);
    }
    put(this.handles[7], this.screen(boxPoint(vol, [1, 1, 0], R)));
  }

  private xy(e: PointerEvent): [number, number] {
    const r = this.canvas.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  }

  private yawAt(x: number, y: number, c: V3): number | null {
    const { o, d } = this.v.cam.ray(x, y);
    if (Math.abs(d[2]) < 1e-3) return null;
    const t = (c[2] - o[2]) / d[2];
    if (t < 0) return null;
    return Math.atan2(o[1] + d[1] * t - c[1], o[0] + d[0] * t - c[0]);
  }

  private down(e: PointerEvent, i: number) {
    const vol = this.sel;
    if (!vol || e.button > 0) return;
    e.preventDefault(); e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    const [x, y] = this.xy(e), cam = this.v.cam, v0 = copy(vol);
    if (i < 6) {
      const k = i >> 1, s = i & 1 ? -1 : 1, u: V3 = [0, 0, 0];
      u[k] = s;
      const p = boxPoint(vol, u), a = scale(axis(rotation(vol.ypr), k), s);
      const p0 = this.screen(p), p1 = this.screen(add(p, scale(a, 0.01)));
      if (!p0 || !p1) return;
      this.drag = { t: 'face', k, s, x0: x, y0: y, v0, sv: [(p1[0] - p0[0]) / 0.01, (p1[1] - p0[1]) / 0.01] };
    } else if (i === 6) {
      const depth = Math.max(0.2, dot(sub(vol.center, cam.e), cam.f));
      this.drag = { t: 'move', x0: x, y0: y, v0, k: cam.ortho ? cam.ps : (2 * Math.tan(cam.fov / 2) * depth) / cam.h };
    } else {
      const a0 = this.yawAt(x, y, vol.center);
      if (a0 !== null) this.drag = { t: 'rot', v0, a0 };
    }
  }

  private move(e: PointerEvent) {
    const d = this.drag, vol = this.sel;
    if (!d || !vol) return;
    const [x, y] = this.xy(e), cam = this.v.cam;
    if (d.t === 'face') {
      // metres along the face normal = mouse motion projected on its screen direction
      const n2 = d.sv[0] ** 2 + d.sv[1] ** 2;
      if (n2 < 1e-6) return;
      let t = ((x - d.x0) * d.sv[0] + (y - d.y0) * d.sv[1]) / n2;
      t = Math.max(t, 0.02 - d.v0.size[d.k]);
      vol.size[d.k] = d.v0.size[d.k] + t;
      vol.center = add(d.v0.center, scale(axis(rotation(vol.ypr), d.k), (d.s * t) / 2));
    } else if (d.t === 'move') {
      const dx = (x - d.x0) * d.k, dy = (y - d.y0) * d.k;
      vol.center = add(d.v0.center, sub(scale(cam.r, dx), scale(cam.u, dy)));
    } else {
      const a = this.yawAt(x, y, vol.center);
      if (a === null) return;
      let yaw = d.v0.ypr[0] + ((a - d.a0) * 180) / Math.PI;
      if (e.shiftKey) yaw = Math.round(yaw / 15) * 15;
      vol.ypr[0] = ((yaw + 540) % 360) - 180;
    }
    this.edited();
  }

  private up() {
    if (!this.drag) return;
    this.drag = null;
    this.buildList();
  }

  // ── files ───────────────────────────────────────────────────────────────
  private exportFile() {
    const s = this.v.scene, frame = s ? Object.keys(s.frames)[0] : undefined;
    download('volumes.json', [JSON.stringify(toFile(this.vols, s?.name, frame), null, 1) + '\n'], 'application/json');
  }

  private async importFile() {
    const inp = this.$('vfile') as HTMLInputElement, f = inp.files?.[0];
    inp.value = '';
    if (!f) return;
    try {
      const vols = parseVolumes(JSON.parse(await f.text()));
      if (this.vols.length && !confirm(`Replace the ${this.vols.length} box(es) here with ${vols.length} from ${f.name}?`)) return;
      this.v.s.volumes = vols;
      this.sel = null;
      this.changed();
    } catch (e) { alert(`Could not read ${f.name}: ${(e as Error).message}`); }
  }
}

/** Offset for a duplicate: one box length along its own x. */
function boxAxisOffset(v: Volume): V3 {
  return scale(axis(rotation(v.ypr), 0), v.size[0]);
}
